/**
 * Numeric and date primitives for the `causal_output` derivation.
 *
 * Split out of `causalOutputMapper.ts` so the mapper stays a readable derivation and both halves
 * stay pure: no Vue, no store, no `shared/http`. Every helper is total — the payload is an
 * unversioned backend blob (BE-13-03), so a missing, null or string-typed field yields a defined
 * value instead of `NaN` propagating into the table.
 */

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Coerces an unversioned payload value to a finite number, or `null`.
 *
 * `parseFloat` semantics for strings mirror v3's `parseFloat(value)` reads (so `'12.3%'` still
 * measures 12.3); unlike v3 a non-numeric value can never leak `NaN` into arithmetic.
 */
export function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/**
 * Mean of one metric column over the given timeseries rows.
 *
 * v3: `rows.reduce((sum, d) => sum + (d[m] || 0), 0) / rows.length`, with an empty window
 * averaging to 0. The `|| 0` there string-concatenates when the backend sends `"12"`; coercing
 * first keeps the average numeric.
 */
export function averageOf(rows: readonly Readonly<Record<string, unknown>>[], key: string): number {
  if (rows.length === 0) return 0
  const total = rows.reduce((sum, row) => sum + (toFiniteNumber(row[key]) ?? 0), 0)
  return total / rows.length
}

/** v3's `(post − pre) / |pre| * 100`, keeping its div-by-zero convention of exactly `0`. */
export function percentChange(preAvg: number, postAvg: number): number {
  if (preAvg === 0) return 0
  return ((postAvg - preAvg) / Math.abs(preAvg)) * 100
}

function toTimestamp(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * Inclusive day count between two ISO dates (`round(|end − start| / 1 day) + 1`).
 *
 * `null` when either bound is missing or unparseable — v3 built `NaN` there and rendered
 * "over NaN days".
 */
export function inclusiveDayCount(start: unknown, end: unknown): number | null {
  const from = toTimestamp(start)
  const to = toTimestamp(end)
  if (from === null || to === null) return null
  return Math.round(Math.abs(to - from) / DAY_MS) + 1
}

/**
 * v3's pre/post window filter: a lexicographic ISO-date comparison against `[start, end]`.
 * A partial period matches nothing, which is what v3's `undefined` comparisons already did.
 */
export function isWithinPeriod(date: unknown, period: readonly string[] | null): boolean {
  const start = period?.[0]
  const end = period?.[1]
  if (typeof date !== 'string' || start === undefined || end === undefined) return false
  return date >= start && date <= end
}
